import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import { fetchPartner, fetchBlackoutDays, deleteBlackoutDay } from "api";
import { PageHeader, Table, Spinner, EmptyState } from "components/common";
import { useAuth } from "hooks/useAuth";
import type { BlackoutDay } from "types";

// All blackout days for a given partner, with admin delete capability.

export default function BlackoutDaysPage() {
  const { id }        = useParams<{ id: string }>();
  const navigate      = useNavigate();
  const { isAdmin }   = useAuth();
  const queryClient   = useQueryClient();
  const partnerId     = Number(id);

  const { data: partner } = useQuery({
    queryKey: ["partners", partnerId],
    queryFn:  () => fetchPartner(partnerId),
  });

  const { data: blackoutDays = [], isLoading } = useQuery({
    queryKey: ["blackoutDays", partnerId],
    queryFn:  () => fetchBlackoutDays({ partner_entity_id: partnerId }),
  });

  const { mutate: remove } = useMutation({
    mutationFn: (blackoutDayId: number) => deleteBlackoutDay(blackoutDayId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["blackoutDays", partnerId] });
    },
  });

  if (isLoading) return <Spinner className="mt-20" />;

  return (
    <div className="px-4 py-6 sm:px-8 sm:py-8 max-w-3xl">
      <button
        onClick={() => navigate(`/partners/${partnerId}`)}
        className="text-sm text-gray-400 hover:text-gray-600 flex items-center gap-1 transition-colors mb-6"
      >
        ← {partner?.name ?? "Partner"}
      </button>

      <PageHeader
        title="Blackout days"
        subtitle={`All blackout days for ${partner?.name ?? "this partner"}`}
      />

      <div className="card">
        {blackoutDays.length === 0 ? (
          <EmptyState message="No blackout days on record." />
        ) : (
          <Table
            data={blackoutDays}
            keyExtractor={(b: BlackoutDay) => b.id}
            columns={[
              {
                header:   "Date",
                accessor: (b: BlackoutDay) => format(parseISO(b.date), "EEEE, MMMM d, yyyy"),
              },
              {
                header:   "Added",
                accessor: (b: BlackoutDay) => format(parseISO(b.created_at), "MMM d, yyyy"),
              },
              ...(isAdmin ? [{
                header:   "Actions",
                accessor: (b: BlackoutDay) => (
                  <button
                    className="text-xs text-red-500 hover:text-red-700 transition-colors"
                    onClick={(e) => { e.stopPropagation(); remove(b.id); }}
                  >
                    Remove
                  </button>
                ),
              }] : []),
            ]}
          />
        )}
      </div>
    </div>
  );
}
